import { injectable } from 'inversify';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import type { Connection, Shape } from 'bpmn-js/lib/model/Types';
import Modeling from 'bpmn-js/lib/features/modeling/Modeling';
import ElementFactory from 'bpmn-js/lib/features/modeling/ElementFactory';
import Overlays from 'diagram-js/lib/features/overlays/Overlays';
import { FLOW_LABEL_INDENT } from 'bpmn-js/lib/util/LabelUtil';
// eslint-disable-next-line no-restricted-imports
import type Canvas from 'diagram-js/lib/core/Canvas';
// eslint-disable-next-line no-restricted-imports
import type ElementRegistry from 'diagram-js/lib/core/ElementRegistry';
import type { Point, RectTRBL as Rect } from 'diagram-js/lib/util/Types';

import {
  GROUP_PADDING,
  GROUP_PADDING_BOTTOM,
  HALF,
  NODE_HEIGHT,
  NODE_WIDTH,
  SYSTEM_NODE_HEIGHT,
  BRANCH_LABEL_X_OFFSET,
  FLOW_LABEL_TSPAN_PADDING,
  RETURN_ROUTE_GAP,
  RETURN_ROUTE_LABEL_HEIGHT,
} from '../../constants/eventTypes';
import type {
  CanvasElementEvent,
  FlowNode,
  FlowSchema,
  Segment,
} from '../../types/event-types';

import { EventTypesLayoutService } from './layout.service';
import { EventTypesReturnConnectionService } from './return-connection.service';

const MAIN_BRANCH_LABEL = 'Основное действие';
const SECONDARY_BRANCH_LABEL = 'Второе действие';
const BRANCH_LABEL_MARKER = 'branch-label';

const MAIN_BRANCH_LABEL_WIDTH = 136;
const SECONDARY_BRANCH_LABEL_WIDTH = 119;

const CONNECTION_BORDER_WIDTH = 2;
const LABEL_OVERHEAD_X = FLOW_LABEL_INDENT + FLOW_LABEL_TSPAN_PADDING;

@injectable()
export class EventTypesCanvasService {
  constructor(
    private readonly layoutService: EventTypesLayoutService,
    private readonly returnConnectionService: EventTypesReturnConnectionService,
  ) {}

  /** Создаёт оверлеи для каждого узла схемы, используемые для рендера кастомных нод */
  addNodeOverlays(
    modeler: BpmnModeler,
    schema: FlowSchema,
    schemaIdToBpmnId: Map<string, string>,
  ): Map<string, HTMLDivElement> {
    const overlays = modeler.get<Overlays>('overlays');
    const overlayMap = new Map<string, HTMLDivElement>();

    for (const node of schema.nodes) {
      const bpmnId = schemaIdToBpmnId.get(node.id);
      if (!bpmnId) continue;

      const container = document.createElement('div');
      overlays.add(bpmnId, {
        position: { top: 0, left: 0 },
        html: container,
      });
      overlayMap.set(node.id, container);
    }

    return overlayMap;
  }

  /** Создаёт оверлеи-подложки для параллельных групп нужного размера */
  addGroupOverlays(
    modeler: BpmnModeler,
    schema: FlowSchema,
    schemaIdToBpmnId: Map<string, string>,
    positions: Map<string, Point>,
  ): Map<string, HTMLDivElement> {
    const overlays = modeler.get<Overlays>('overlays');
    const overlayMap = new Map<string, HTMLDivElement>();
    const parallelGroups = this.computeParallelGroups(schema.nodes);

    for (const [groupId, nodeIds] of parallelGroups) {
      if (nodeIds.length < HALF) continue;
      const bpmnId = schemaIdToBpmnId.get(groupId);
      if (!bpmnId) continue;

      const bounds = this.computeGroupBounds(nodeIds, positions);
      if (!bounds) continue;

      const container = document.createElement('div');
      container.style.width = `${bounds.width}px`;
      container.style.height = `${bounds.height}px`;
      overlays.add(bpmnId, {
        position: { top: 0, left: 0 },
        html: container,
      });
      overlayMap.set(groupId, container);
    }

    return overlayMap;
  }

  /** Строит ортогональные waypoints */
  private getOrthogonalWaypoints(source: Shape, target: Shape) {
    const startX = source.x + source.width / HALF;
    const startY = source.y + source.height;
    const endX = target.x + target.width / HALF;
    const endY = target.y;
    const midY = (startY + endY) / HALF;

    return [
      { x: startX, y: startY },
      { x: startX, y: midY },
      { x: endX, y: midY },
      { x: endX, y: endY },
    ];
  }

  /** Строит ортогональные waypoints для параллельных групп */
  private getOrthogonalWaypointsToGroup(source: Shape, group: Shape) {
    const offset = { ...group, y: group.y + GROUP_PADDING };

    return this.getOrthogonalWaypoints(source, offset);
  }

  private getBranchLabelBounds(
    edge: FlowSchema['edges'][number],
    source: Shape,
  ): { x: number; y: number; width: number; height: number } | null {
    if (edge.condition !== 'main' && edge.condition !== 'secondary') {
      return null;
    }

    const startX = source.x + source.width / HALF;

    if (edge.condition === 'main') {
      return {
        x:
          startX -
          MAIN_BRANCH_LABEL_WIDTH -
          LABEL_OVERHEAD_X -
          BRANCH_LABEL_X_OFFSET,
        y: 0,
        width: MAIN_BRANCH_LABEL_WIDTH,
        height: RETURN_ROUTE_LABEL_HEIGHT,
      };
    }

    return {
      x:
        startX -
        LABEL_OVERHEAD_X +
        CONNECTION_BORDER_WIDTH +
        BRANCH_LABEL_X_OFFSET,
      y: 0,
      width: SECONDARY_BRANCH_LABEL_WIDTH,
      height: RETURN_ROUTE_LABEL_HEIGHT,
    };
  }

  private getBranchLabelObstacleRect(
    edge: FlowSchema['edges'][number],
    source: Shape,
  ): Rect | null {
    return this.returnConnectionService.getBranchLabelObstacleRect(
      edge,
      source,
    );
  }

  /** Обновляет текст лейбла на стрелке связи */
  private updateBranchLabel(
    modeling: Modeling,
    canvas: Canvas,
    connection: Connection,
    label: string,
    labelBounds: { x: number; y: number; width: number; height: number },
  ) {
    modeling.updateLabel(connection, label, labelBounds);

    if (connection.label) {
      canvas.addMarker(connection.label, BRANCH_LABEL_MARKER);
    }
  }

  /** Ставит лейбл ветки (main/secondary) слева или справа от стрелки */
  private applyBranchLabel(
    modeling: Modeling,
    canvas: Canvas,
    connection: Connection,
    edge: FlowSchema['edges'][number],
    source: Shape,
  ): Rect | null {
    const labelBounds = this.getBranchLabelBounds(edge, source);
    const labelObstacleRect = this.getBranchLabelObstacleRect(edge, source);

    if (!labelBounds || !labelObstacleRect) {
      return null;
    }

    if (edge.condition === 'main') {
      this.updateBranchLabel(
        modeling,
        canvas,
        connection,
        MAIN_BRANCH_LABEL,
        labelBounds,
      );
    }

    if (edge.condition === 'secondary') {
      this.updateBranchLabel(
        modeling,
        canvas,
        connection,
        SECONDARY_BRANCH_LABEL,
        labelBounds,
      );
    }

    return labelObstacleRect;
  }

  getShapeBySchemaId(
    modeler: BpmnModeler,
    schemaIdToBpmnId: Map<string, string>,
    schemaId: string,
  ): Shape | null {
    const bpmnId = schemaIdToBpmnId.get(schemaId);

    if (!bpmnId) {
      return null;
    }

    const elementRegistry = modeler.get<ElementRegistry>('elementRegistry');
    const element = elementRegistry.get(bpmnId);

    return element && 'width' in element && 'height' in element
      ? (element as Shape)
      : null;
  }

  /** Строит диаграмму по схеме */
  async buildDiagram(
    modeler: BpmnModeler,
    schema: FlowSchema,
  ): Promise<{
    schemaIdToBpmnId: Map<string, string>;
    positions: Map<string, Point>;
  }> {
    const schemaIdToBpmnId = new Map<string, string>();

    const positions = this.layoutService.computeLayout(schema);

    await modeler.createDiagram();

    if (!schema.nodes.length) return { schemaIdToBpmnId, positions };

    const modeling = modeler.get<Modeling>('modeling');
    const elementFactory = modeler.get<ElementFactory>('elementFactory');
    const elementRegistry = modeler.get<ElementRegistry>('elementRegistry');
    const canvas = modeler.get<Canvas>('canvas');

    const defaultStartEvent = elementRegistry.filter(
      (element) => element.type === 'bpmn:StartEvent',
    )[0] as Shape | undefined;
    if (defaultStartEvent) {
      modeling.removeElements([defaultStartEvent]);
    }

    const processElement = elementRegistry.filter(
      (element) => element.type === 'bpmn:Process',
    )[0] as Shape;

    const parallelGroups = this.computeParallelGroups(schema.nodes);
    const nodeToGroupId = new Map<string, string>();
    for (const [groupId, nodeIds] of parallelGroups) {
      for (const nodeId of nodeIds) {
        nodeToGroupId.set(nodeId, groupId);
      }
    }

    const shapeByGroupId = new Map<string, Shape>();
    for (const [groupId, groupNodeIds] of parallelGroups) {
      if (groupNodeIds.length < HALF) continue;

      const bounds = this.computeGroupBounds(groupNodeIds, positions);
      if (!bounds) continue;

      const groupShape = modeling.createShape(
        elementFactory.createShape({ type: 'bpmn:Group', isExpanded: false }),
        {
          x: bounds.x + bounds.width / HALF,
          y: bounds.y + bounds.height / HALF,
        },
        processElement,
      );
      modeling.resizeShape(groupShape, {
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height,
      });
      shapeByGroupId.set(groupId, groupShape);
      schemaIdToBpmnId.set(groupId, groupShape.id);
    }

    const shapeByNodeId = new Map<string, Shape>();
    for (const node of schema.nodes) {
      const position = positions.get(node.id);
      if (!position) continue;

      const nodeHeight =
        node.type === 'SystemTask' ? SYSTEM_NODE_HEIGHT : NODE_HEIGHT;
      const centerX = position.x + NODE_WIDTH / HALF;
      const centerY = position.y + nodeHeight / HALF;
      const shape = modeling.createShape(
        elementFactory.createShape({ type: 'bpmn:UserTask' }),
        { x: centerX, y: centerY },
        processElement,
      );
      modeling.resizeShape(shape, {
        x: position.x,
        y: position.y,
        width: NODE_WIDTH,
        height: nodeHeight,
      });
      shapeByNodeId.set(node.id, shape);
      schemaIdToBpmnId.set(node.id, shape.id);
    }

    const createdConnections = new Set<string>();
    const connectionSegments: Segment[] = [];
    const labelObstacleRects: Rect[] = [];
    const nodeById = new Map(schema.nodes.map((node) => [node.id, node]));
    const nodeObstacles = schema.nodes
      .map((node) => ({
        parallelGroupId: node.parallelGroupId ?? null,
        shape: shapeByNodeId.get(node.id),
      }))
      .filter((nodeObstacle) => Boolean(nodeObstacle.shape))
      .map((nodeObstacle) => ({
        parallelGroupId: nodeObstacle.parallelGroupId,
        shape: nodeObstacle.shape as Shape,
      }));
    const groupObstacles = [...shapeByGroupId.entries()].map(
      ([schemaId, shape]) => ({
        schemaId,
        shape,
      }),
    );
    const groupObstacleBySchemaId = new Map(shapeByGroupId.entries());

    const resolveEndpoint = (nodeId: string) =>
      nodeToGroupId.get(nodeId) ?? nodeId;

    for (const edge of schema.edges) {
      const sourceEndpoint = resolveEndpoint(edge.sourceId);
      const targetEndpoint = resolveEndpoint(edge.targetId);

      if (sourceEndpoint === targetEndpoint) continue;

      const key = `${sourceEndpoint}-${targetEndpoint}`;
      if (createdConnections.has(key)) continue;
      createdConnections.add(key);

      const source =
        shapeByGroupId.get(sourceEndpoint) ?? shapeByNodeId.get(edge.sourceId);
      const target =
        shapeByGroupId.get(targetEndpoint) ?? shapeByNodeId.get(edge.targetId);
      if (!source || !target) continue;

      const connection = modeling.connect(source, target, {
        type: 'bpmn:SequenceFlow',
      });
      const sourceInGroup = nodeToGroupId.has(edge.sourceId);
      const targetInGroup = nodeToGroupId.has(edge.targetId);
      const waypoints = targetInGroup
        ? this.getOrthogonalWaypointsToGroup(source, target)
        : this.getOrthogonalWaypoints(source, target);

      modeling.layoutConnection(connection, {
        waypoints,
      });
      connectionSegments.push(
        ...this.returnConnectionService.toSegments(waypoints),
      );

      if (!sourceInGroup) {
        const labelRect = this.applyBranchLabel(
          modeling,
          canvas,
          connection,
          edge,
          source,
        );

        if (labelRect) {
          labelObstacleRects.push(labelRect);
        }
      }

      if (!sourceInGroup && !targetInGroup) {
        schemaIdToBpmnId.set(edge.id, connection.id);
      }
    }

    for (const returnEdge of schema.returns ?? []) {
      const source = shapeByNodeId.get(returnEdge.sourceId);
      const target = shapeByNodeId.get(returnEdge.targetId);

      if (!source || !target) {
        continue;
      }

      const connection = modeling.connect(source, target, {
        type: 'bpmn:SequenceFlow',
      });
      const sourceNode = nodeById.get(returnEdge.sourceId);
      const targetNode = nodeById.get(returnEdge.targetId);
      const returnWaypoints = this.returnConnectionService.buildReturnRoute(
        source,
        target,
        {
          nodeObstacles,
          groupObstacles,
          groupObstacleBySchemaId,
          labelObstacleRects,
          connectionObstacles: connectionSegments,
          sourceParallelGroupId: sourceNode?.parallelGroupId ?? null,
          targetParallelGroupId: targetNode?.parallelGroupId ?? null,
          preferTopCenterSource: Boolean(sourceNode?.parallelGroupId),
          preferTopCenterTarget: Boolean(targetNode?.parallelGroupId),
          gap: RETURN_ROUTE_GAP,
        },
      );

      modeling.layoutConnection(connection, {
        waypoints: returnWaypoints,
      });
      connectionSegments.push(
        ...this.returnConnectionService.toSegments(returnWaypoints),
      );
      schemaIdToBpmnId.set(returnEdge.id, connection.id);
    }

    return { schemaIdToBpmnId, positions };
  }

  /** Проверяет, что элемент является узлом схемы */
  isNode(element: CanvasElementEvent['element']): boolean {
    return (
      !!element &&
      !element.waypoints &&
      element.type !== 'label' &&
      element.type !== 'bpmn:Group' &&
      element.type !== 'bpmn:Process'
    );
  }

  /** Вычисляет box параллельной группы с учётом padding */
  private computeGroupBounds(
    nodeIds: string[],
    positions: Map<string, Point>,
  ): { x: number; y: number; width: number; height: number } | undefined {
    const firstPos = positions.get(nodeIds[0]);
    if (!firstPos) return undefined;

    const innerWidth = this.layoutService.groupInnerWidth(nodeIds.length);

    return {
      x: firstPos.x - GROUP_PADDING,
      y: firstPos.y - GROUP_PADDING,
      width: innerWidth + HALF * GROUP_PADDING,
      height: NODE_HEIGHT + GROUP_PADDING + GROUP_PADDING_BOTTOM,
    };
  }

  /** Группирует nodeId по parallelGroupId */
  private computeParallelGroups(nodes: FlowNode[]): Map<string, string[]> {
    const groups = new Map<string, string[]>();
    for (const node of nodes) {
      if (node.parallelGroupId) {
        const existing = groups.get(node.parallelGroupId) ?? [];
        existing.push(node.id);
        groups.set(node.parallelGroupId, existing);
      }
    }
    return groups;
  }
}
